import { AxiosError } from 'axios';
import { Upload } from 'lucide-react';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';

import { FileUpload } from './components/FileUpload';

import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/Card';
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselNext,
  CarouselPrevious,
} from '@/components/ui/Carousel';
import { useUploadProductImages } from '@/hooks/useUploadProductImages';
import type { UploadedImageFile } from '@/services/ProductImagesService';

export function ImagesManager() {
  const { t } = useTranslation('translation', {
    keyPrefix: 'pages.Settings.components.ImagesManager',
  });

  const { uploadImages, isLoading: isUploading } = useUploadProductImages();
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const [uploadProgress, setUploadProgress] = useState<number | null>(null);
  const [uploaded, setUploaded] = useState<UploadedImageFile[]>([]);

  async function handleFileUpload(files: File[]) {
    if (!files || files.length === 0) return;

    try {
      setUploadProgress(0);
      const result = await uploadImages({
        files,
        concurrency: 6,
        onProgress: (pct) => setUploadProgress(pct),
      });
      toast.success(
        t('components.FileUpload.toast.uploadSuccess', {
          filename: `${files.length} files`,
        }),
      );
      setUploaded((prev) => [...result, ...prev]);
      setSelectedFiles([]);
    } catch (error) {
      const isAxiosError = error instanceof AxiosError;

      const isPayloadTooLarge = isAxiosError
        ? error.response?.status === 413
        : (error as any)?.statusCode === 413;

      toast.error(
        t('components.FileUpload.toast.uploadError', {
          filename: `${files.length} files`,
          error: isPayloadTooLarge
            ? t('components.FileUpload.toast.fileTooLarge')
            : String(error),
        }),
      );
    } finally {
      setUploadProgress(null);
    }
  }

  const uploadedPreview = useMemo(() => uploaded.slice(0, 20), [uploaded]);
  const isCarouselActive = uploadedPreview.length > 4;

  return (
    <Card>
      <CardHeader>
        <CardTitle>{t('title')}</CardTitle>
        <CardDescription className="text-muted-foreground">
          {t('description')}
        </CardDescription>
      </CardHeader>

      <CardContent className="space-y-4">
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Upload className="h-5 w-5" />
              {t('components.FileUpload.title')}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <FileUpload
              onSetSelectedFiles={setSelectedFiles}
              selectedFiles={selectedFiles}
              onFileUpload={handleFileUpload}
              isLoading={isUploading}
              uploadProgress={uploadProgress}
            />
          </CardContent>
        </Card>

        {uploadedPreview.length > 0 && (
          <Card className="w-full">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                {t('components.UploadedList.title')}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <Carousel
                opts={{
                  align: 'start',
                  active: isCarouselActive,
                }}
                className="w-full"
              >
                <CarouselContent className="w-full">
                  {uploadedPreview.map((f) => (
                    <CarouselItem
                      key={f.key}
                      className="sm:basis-1/2 lg:basis-1/4"
                    >
                      <img
                        src={f.url}
                        alt={f.key}
                        className="h-28 w-full rounded object-cover"
                      />
                    </CarouselItem>
                  ))}
                </CarouselContent>

                {isCarouselActive && (
                  <>
                    <CarouselPrevious className="left-0" />
                    <CarouselNext className="right-0" />
                  </>
                )}
              </Carousel>
            </CardContent>
          </Card>
        )}
      </CardContent>
    </Card>
  );
}
